import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';

interface HotelPricingAssistantPmsSsEntryPointBannerSignature {
  Args: {
    bannerMessage: string;
  };
}

export default class HotelPricingAssistantPmsSsEntryPointBanner extends Component<HotelPricingAssistantPmsSsEntryPointBannerSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare intl: Services['intl'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service declare router: Services['router'];

  handleConnectPmsRequestTask = task(
    { drop: true },
    async (): Promise<void> => {
      const result =
        await this.pricingAssistantPmsSs.launchConnectPmsRequestTask.perform();

      if (result.isSuccess) {
        // Update feature flags for changed in-app states
        await this.access.refetchFeatureFlags();

        this.router.transitionTo('hotel.settings.pms-integration');
      } else {
        this.errorTracking.captureMessage(
          'Pricing Assistant Connect PMS handling failed',
          {
            error: result.error,
          },
        );

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      }
    },
  );

  get isRequestInProgress(): boolean {
    return this.pricingAssistantPmsSs.launchConnectPmsRequestTask.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryAction() {
    return {
      label: this.intl.t(
        'pricing_assistant.self_service.banner.action.connect_pms',
      ),
      // eslint-disable-next-line @typescript-eslint/unbound-method
      action: this.onConnectPMSButtonClicked,
      disabled: this.isRequestInProgress,
    };
  }

  @action
  onConnectPMSButtonClicked(): void {
    // Only launch the connectPmsRequest in case the user has PA settings edit rights (otherwise BE will block it anyway)
    // For now users without edit rights can click the Connect PMS button, but they will just be redirected to the PMS integrations page.
    // They'll see an info message with some explanation there.
    if (this.access.hasPricingAssistantSettingsEditRights) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.handleConnectPmsRequestTask.perform();
    } else {
      this.router.transitionTo('hotel.settings.pms-integration');
    }
  }

  <template>
    <PrismBanner
      @bannerMessage={{@bannerMessage}}
      @bannerStyle="brand"
      @hasCta={{true}}
      @icon="connection"
      @isOneLine={{true}}
      @primaryAction={{this.primaryAction}}
    />
  </template>
}
